export interface ProfileImage {
  id: string; // 64-hex sha256
  url: string; // appdata://vto-input/<id>.jpg
  aspectRatio: number;
}

export interface VtoResult {
  url: string; // appdata://vto/<key>.jpg
  aspectRatio: number;
  expiresAt: number; // Unix epoch ms
}

export type VtoLookup = VtoResult | null;

export interface PrefetchItem {
  syan: number;
  colorId: string;
}

export type VtoErrorCode =
  | 'invalidArgument'
  | 'unknownProfileImage'
  | 'generationFailed'
  | 'rateLimited'
  | 'networkError'
  | 'configurationFailed'
  | 'internalError'
  | 'userCanceled';

export type VtoErrorSource = 'getVto' | 'prefetchVtos' | 'getProfileImage' | 'requestProfileImage';

export interface VtoError {
  source?: VtoErrorSource;
  code: VtoErrorCode;
  message?: string;
  backendErrorCode?: VtoBackendErrorCode;
  syan?: number;
  colorId?: string;
}

export type VtoBackendErrorCode =
  | 'ARTICLE_NOT_FOUND'
  | 'CHILD_IN_IMAGE'
  | 'CUSTOMER_RATE_LIMIT_EXCEEDED'
  | 'GLOBAL_MONTHLY_LIMIT_EXCEEDED'
  | 'IMAGE_NOT_FOUND'
  | 'IMAGE_TOO_LARGE'
  | 'INVALID_IMAGE_FORMAT'
  | 'INVALID_REQUEST_PARAMETERS'
  | 'JOB_NOT_FOUND'
  | 'MODEL_NOT_FOUND'
  | 'NO_PERSON_FOUND'
  | 'PROCESSING_TIMEOUT'
  | 'PRODUCT_NOT_FOUND'
  | 'QUOTA_EXCEEDED'
  | 'SAFETY_SETTING_VIOLATION'
  | 'SERVICE_UNAVAILABLE'
  | 'UNKNOWN_ERROR';

// Single source of truth. Adding a code to VtoBackendErrorCode without classifying it here
// fails the Record exhaustiveness check at compile time.
export const BACKEND_CODE_POLICY: Record<VtoBackendErrorCode, BackendErrorPolicy> = {
  CHILD_IN_IMAGE: 'profile-recovery',
  IMAGE_TOO_LARGE: 'profile-recovery',
  INVALID_IMAGE_FORMAT: 'profile-recovery',
  NO_PERSON_FOUND: 'profile-recovery',
  SAFETY_SETTING_VIOLATION: 'profile-recovery',
  ARTICLE_NOT_FOUND: 'permanent',
  INVALID_REQUEST_PARAMETERS: 'permanent',
  MODEL_NOT_FOUND: 'permanent',
  PRODUCT_NOT_FOUND: 'permanent',
  GLOBAL_MONTHLY_LIMIT_EXCEEDED: 'session-suspend',
  QUOTA_EXCEEDED: 'session-suspend',
  CUSTOMER_RATE_LIMIT_EXCEEDED: 'cooldown',
  PROCESSING_TIMEOUT: 'cooldown',
  SERVICE_UNAVAILABLE: 'cooldown',
  IMAGE_NOT_FOUND: 'transient',
  JOB_NOT_FOUND: 'transient',
  UNKNOWN_ERROR: 'transient',
};

// Subset of RetryPolicy['kind'] reachable from a backend error code. The remaining RetryPolicy
// variants ('no-op', 'vto-disabled') come from top-level VtoErrorCode classification only.
type BackendErrorPolicy = 'profile-recovery' | 'permanent' | 'session-suspend' | 'cooldown' | 'transient';

export type RetryPolicy =
  | { kind: 'no-op' }
  | { kind: 'permanent' }
  | { kind: 'profile-recovery' }
  | { kind: 'vto-disabled' }
  | { kind: 'session-suspend' }
  | { kind: 'cooldown'; cooldownMs: number }
  | { kind: 'transient' };

export type VtoStatus =
  | { kind: 'ok' }
  | { kind: 'cooldown'; until: number }
  | { kind: 'profile-invalid' }
  | { kind: 'vto-disabled' }
  | { kind: 'session-suspended' };

// `(string & {})` preserves autocomplete for the known values without rejecting unknown ones the bridge may send.
export type VtoBridgeProgressStatus = 'PROCESSING' | 'COMPLETED' | 'FAILED' | (string & {});

export interface VtoProgressStatus {
  source: 'getVto' | 'prefetchVtos';
  batchJobId?: string;
  status: VtoBridgeProgressStatus;
  progress: number;
  totalArticles: number;
  completedArticles: number;
  failedArticles: number;
  estimatedRemainingTimeSeconds?: number;
}

export interface VtoBridge {
  requestProfileImage: () => Promise<ProfileImage | null>;
  getProfileImage: () => Promise<ProfileImage | null>;
  getVto: (arg: { profileImage: ProfileImage; syan: number; colorId: string; ttl: number }) => Promise<VtoResult>;
  prefetchVtos: (arg: { profileImage: ProfileImage; items: PrefetchItem[]; ttl: number }) => Promise<void>;
  /**
   * Single-subscriber slots owned by VtoService. VtoService takes over both slots on first use
   * and releases them on destroy(); concurrent consumers will displace each other (a warning is
   * logged when replacement happens). Do not assign from anywhere except a VtoService instance.
   */
  _onProgress?: (s: VtoProgressStatus) => void;
  _onError?: (e: VtoError) => void;
}

export const isProfileImage = (x: unknown): x is ProfileImage =>
  typeof x === 'object' &&
  x !== null &&
  typeof (x as ProfileImage).id === 'string' &&
  typeof (x as ProfileImage).url === 'string' &&
  typeof (x as ProfileImage).aspectRatio === 'number';

export const isVtoResult = (x: unknown): x is VtoResult =>
  typeof x === 'object' &&
  x !== null &&
  typeof (x as VtoResult).url === 'string' &&
  typeof (x as VtoResult).aspectRatio === 'number' &&
  typeof (x as VtoResult).expiresAt === 'number';
